import { Box, Button, Flex, Loader, Text } from '@mantine/core';
import type { FC } from 'react';
import { Icon } from '@/components';
import { Flag } from 'react-feather';
import { useDisclosure } from '@mantine/hooks';
import { RedFlagWarningInfo } from './RedFlagWarningInfo';
import {
  useSmallScreen,
  useWindowDimensions,
} from '@/hooks/useWindowDimension';
import {
  type RedFlagWarning,
  useGetRedFlagWarningsQuery,
} from '@/features/wildfireSlice';
import { isHttpError } from '@/features/utils';
import React, { useCallback, useEffect, useMemo } from 'react';
import ModalComponent from '@/components/Modals/Modal';
import { useClientTerminology } from '@/hooks/useClientTerminology';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { setQuery } from '@/features/filterSlice';
import {
  DESKTOP_VIEWBOX_PADDING,
  MOBILE_VIEWBOX_PADDING,
  updateViewport,
} from '@/views/map/deckglUtils';
import { updateViewState } from '@/features/analysisMapSlice';
import { GoogleEvents } from '@/features/googleEvents';
import TagManager from 'react-gtm-module';
import { useTrackFilterChange } from '@/views/filter/useTrackFilterChange';

import classes from './RedFlagWarningCard.module.css';
import featureBarClasses from '@/views/feature-bar/FeatureBar.module.css';
import { RedFlagWarningCountBadge } from '@/views/map/RedFlagWarning/RedFlagWarningCountBadge';
import { NoRedFlagWarningInfo } from '@/views/map/RedFlagWarning/NoRedFlagWarningInfo';

interface Props {
  setRedFlagWarnings: React.Dispatch<
    React.SetStateAction<RedFlagWarning[] | undefined>
  >;
}

export const RedFlagWarningCard: FC<Props> = ({ setRedFlagWarnings }) => {
  const {
    data: redFlagWarnings,
    isLoading,
    isError,
    error,
  } = useGetRedFlagWarningsQuery(null);

  const isNotAvailable = isHttpError(error, 404);

  const intl = useClientTerminology();
  const dispatch = useAppDispatch();
  const trackFilterChange = useTrackFilterChange();
  const [opened, { open, close }] = useDisclosure(false);
  const filterState = useAppSelector((state) => state.filterQuery);
  const { filterBoundingBox, viewState } = useAppSelector((s) => s.analysisMap);
  const { width } = useWindowDimensions();

  const handleOnClick = useCallback(() => {
    TagManager.dataLayer({
      dataLayer: {
        event: GoogleEvents.redFlagWarning.card.cardOpened,
      },
    });
    open();
  }, [open]);

  const redFlagWarningsData = redFlagWarnings?.data;

  const handleShowAffectedAreas = useCallback(() => {
    if (!redFlagWarningsData) {
      return;
    }
    TagManager.dataLayer({
      dataLayer: {
        event: GoogleEvents.redFlagWarning.card.showAffectedSpans,
        redFlagWarningFilters: redFlagWarningsData.map((x) => x.description),
      },
    });
    const nextQuery = {
      ...filterState,
      redFlagWarnings: redFlagWarningsData.map((w) => w.id),
    };
    dispatch(setQuery(nextQuery));
    trackFilterChange(nextQuery, 'red_flag_card');
    const updatedViewState = updateViewport(
      viewState,
      filterBoundingBox,
      width,
      DESKTOP_VIEWBOX_PADDING,
      MOBILE_VIEWBOX_PADDING
    );
    dispatch(updateViewState(updatedViewState));
    close();
  }, [
    redFlagWarningsData,
    dispatch,
    close,
    filterState,
    filterBoundingBox,
    viewState,
    width,
    trackFilterChange,
  ]);

  useEffect(() => {
    setRedFlagWarnings(redFlagWarnings?.data);
  }, [redFlagWarnings, setRedFlagWarnings]);

  const isSmallScreen = useSmallScreen();
  const canShowModal = useMemo(
    () => !isLoading && !isError,
    [isLoading, isError]
  );

  if (isNotAvailable) {
    return null;
  }

  return (
    <Box className={classes.cardContainer}>
      <Button
        onClick={handleOnClick}
        leftSection={
          isLoading ? (
            <Loader size="xs" />
          ) : (
            <Icon
              Component={Flag}
              color="var(--mantine-color-redBrand-6)"
              size={16}
            />
          )
        }
        className={featureBarClasses.button}
        disabled={!canShowModal}
      >
        {isError && (
          <Text size="sm">
            {intl.formatMessage({ id: 'redFlagWarning.errorLoading' })}
          </Text>
        )}
        {canShowModal && Array.isArray(redFlagWarnings?.data) && (
          <RedFlagWarningCountBadge count={redFlagWarnings?.data?.length} />
        )}
      </Button>

      {canShowModal && (
        <ModalComponent
          fullScreen={isSmallScreen}
          size="lg"
          opened={opened}
          onClose={close}
          radius="sm"
          title={
            <Flex align="center" gap="xs">
              <Icon
                Component={Flag}
                color="var(--mantine-color-redBrand-6)"
                size={24}
              />
              <Text>{intl.formatMessage({ id: 'redFlagWarning.title' })}</Text>
              <RedFlagWarningCountBadge count={redFlagWarnings?.data.length} />
            </Flex>
          }
          onSecondaryAction={close}
          secondaryLabel={intl.formatMessage({ id: 'redFlagWarning.close' })}
          onPrimaryAction={
            redFlagWarnings?.data?.length ? handleShowAffectedAreas : undefined
          }
          primaryLabel={
            redFlagWarnings?.data?.length
              ? intl.formatMessage({
                  id: 'redFlagWarning.showAffectedSpans',
                })
              : undefined
          }
          className={classes.modalContainer}
        >
          <Box className={classes.modalBody}>
            {redFlagWarnings?.data?.length ? (
              redFlagWarnings.data.map((warning, index) => (
                <RedFlagWarningInfo
                  key={index}
                  title={warning.area}
                  coveredCircuits={warning.coveredCircuits}
                  coveredDistance={warning.coveredDistance}
                  issued={warning.issued_date}
                  start={warning.start_date}
                  end={warning.expiration_date}
                  nwsLink="https://forecast.weather.gov/wwamap/wwatxtget.php?cwa=usa&wwa=red+flag+warning"
                  isPopover={false}
                />
              ))
            ) : (
              <NoRedFlagWarningInfo />
            )}
          </Box>
        </ModalComponent>
      )}
    </Box>
  );
};
